/**
 * Image Cards Component
 * 
 * A component that displays multiple rows of image cards with a heading and button.
 */

import { setAriaHidden } from '../Utils/accessibility-utils';

export interface IImageCard {
    heading: string;
    text?: string;
    image?: {
        src: string;
        alt: string;
    };
    icon?: {
        src: string;
    };
    link?: ILink;
}

interface ILink {
    href: string;
    text: string;
}

type TCardsType = 'image-cards__default' | 'image-cards__without-images' | 'image-cards__with-icons';

export interface IImageCardsOptions {
    className: TCardsType;
    heading: string;
    headingButton: ILink;
    cards: IImageCard[];
}

// Interface for the public methods returned by the component
interface IImageCardsComponent {
    destroy: () => void;
}

export class ImageCards {
    private currentCardIndex = 0;
    private isCarouselMode = false;
    private isAnimating = false;
    private cards: IImageCard[];
    private element: HTMLElement;

    constructor(element: HTMLElement, cards: IImageCard[]) {
        this.element = element;
        this.cards = cards;

        this.init();
    }
    
    // Add a class to indicate if carousel should be enabled or stacked layout should be used
    private checkCarouselMode = (): boolean => {
        const carouselControls = this.element.querySelector('.image-cards__carousel-controls') as HTMLElement;
        // Only enable carousel mode if we have more than 3 cards AND we're on a tablet or smaller device
        return window.getComputedStyle(carouselControls).getPropertyValue('display') !== 'none' && this.cards.length > 1;
    };
    
    // Initialize and add event listeners
    private init = () => {
        window.addEventListener('DOMContentLoaded', () => {
            this.isCarouselMode = this.checkCarouselMode();
            positionCards();
        });

        // Get carousel elements
        const prevButton = this.element.querySelector('.image-cards__carousel-prev') as HTMLButtonElement;
        const nextButton = this.element.querySelector('.image-cards__carousel-next') as HTMLButtonElement;
        const dotButtonsContainer = this.element.querySelector('.image-cards__carousel-dots') as HTMLButtonElement;
        const dotButtons = Array.from(this.element.querySelectorAll('.image-cards__carousel-dot')) as HTMLButtonElement[];
        const cardElements = Array.from(this.element.querySelectorAll('.image-card')) as HTMLElement[];
        const cardsContainer = this.element.querySelector('.image-cards__cards') as HTMLElement;
        
        const equalizeCardHeights = () => {
            cardElements.forEach(card => {
                card.style.height = 'auto';
            });

            if (window.innerWidth > 768 || !this.isCarouselMode) { // Desktop mode when wider than @container-tablet-width
                cardsContainer.style.height = 'auto';
                return;
            };
            let maxHeight = 0;
            cardElements.forEach(card => {
                const height = card.offsetHeight;
                maxHeight = Math.max(maxHeight, height);
                
                card.style.height = '100%';
            });
            
            if (maxHeight > 0) {
                cardsContainer.style.height = `${maxHeight}px`;
            }
        };
        
        // Call the function after the DOM is fully loaded and on resize
        window.addEventListener('DOMContentLoaded', equalizeCardHeights);
        window.addEventListener('load', equalizeCardHeights);
        window.addEventListener('resize', equalizeCardHeights);
        
        // Get previous and next indices safely
        const getPrevIndex = (current: number): number => {
            return current <= 0 ? this.cards.length - 1 : current - 1;
        };
        
        const getNextIndex = (current: number): number => {
            return current >= this.cards.length - 1 ? 0 : current + 1;
        };
        
        // Position all cards initially
        const positionCards = () => {
            if (!this.isCarouselMode) return;
            cardElements.forEach((card, idx) => {
                // Reset all classes first
                card.classList.remove('active', 'prev', 'next');
                if (idx === this.currentCardIndex) {
                    card.classList.add('active');
                    setAriaHidden(card, false);
                    card.style.transform = 'translateX(0)';
                } else if (idx === getPrevIndex(this.currentCardIndex)) {
                    card.classList.add('prev');
                    setAriaHidden(card, true);
                    card.style.transform = 'translateX(-100%)';
                } else if (idx === getNextIndex(this.currentCardIndex)) {
                    card.classList.add('next');
                    setAriaHidden(card, true);
                    card.style.transform = 'translateX(100%)';
                } else {
                    setAriaHidden(card, true);
                    card.style.transform = idx < this.currentCardIndex ? 'translateX(-100%)' : 'translateX(100%)';
                }
            });
            // Update dot indicators
            dotButtons.forEach((dot, idx) => {
                dot.classList.toggle('active', idx === this.currentCardIndex);
            });
        };
        
        // Slide to a specific card with animation
        const slideTo = (newIndex: number, direction: 'left' | 'right') => {
            if (
                this.isAnimating ||
                newIndex === this.currentCardIndex ||
                typeof newIndex !== 'number' ||
                isNaN(newIndex) ||
                newIndex < 0 ||
                newIndex >= cardElements.length
            ) return;
            
            this.isAnimating = true;
            
            const currentCard = cardElements[this.currentCardIndex];
            const newCard = cardElements[newIndex];
            
            // Set up initial positions
            if (direction === 'right') {
                // Current card slides left, new card comes from right
                newCard.style.transform = 'translateX(100%)';
                // Make sure the new card is visible before animating
                newCard.style.opacity = '1';
                newCard.style.zIndex = '2';
                currentCard.style.zIndex = '1';
                
                // Start the animation
                requestAnimationFrame(() => {
                    newCard.style.transition = 'transform 0.5s ease-in-out';
                    currentCard.style.transition = 'transform 0.5s ease-in-out';
                    newCard.style.transform = 'translateX(0)';
                    currentCard.style.transform = 'translateX(-100%)';
                });
                
            } else {
                // Current card slides right, new card comes from left
                newCard.style.transform = 'translateX(-100%)';
                // Make sure the new card is visible before animating
                newCard.style.opacity = '1';
                newCard.style.zIndex = '2';
                currentCard.style.zIndex = '1';
                
                // Start the animation
                requestAnimationFrame(() => {
                    newCard.style.transition = 'transform 0.5s ease-in-out';
                    currentCard.style.transition = 'transform 0.5s ease-in-out';
                    newCard.style.transform = 'translateX(0)';
                    currentCard.style.transform = 'translateX(100%)';
                });
            }
            
            // Update active state after animation finishes
            setTimeout(() => {
                this.currentCardIndex = newIndex;
                cardElements.forEach(card => {
                    card.style.transition = '';
                });
                positionCards();
                dotButtons.forEach((dot, idx) => {
                    dot.classList.toggle('active', idx === this.currentCardIndex);
                });
                setTimeout(() => {
                    this.isAnimating = false;
                }, 350);
            }, 500); // Match the CSS transition time
        };
        
        const goToPrevCard = () => {
            if (this.isCarouselMode) {
                slideTo(getPrevIndex(this.currentCardIndex), 'left');
            }
        };
        
        const goToNextCard = () => {
            if (this.isCarouselMode) {
                slideTo(getNextIndex(this.currentCardIndex), 'right');
            }
        };
        
        const goToCard = (index: number) => {
            if (
                !this.isCarouselMode ||
                index === this.currentCardIndex ||
                typeof index !== 'number' ||
                isNaN(index) ||
                index < 0 ||
                index >= cardElements.length
            ) return;

            let direction: 'left' | 'right';
            direction = index > this.currentCardIndex ? 'right' : 'left';
            slideTo(index, direction);
        };

        // Dot click handler:
        dotButtonsContainer.addEventListener('click', (event) => {
            const dot = event.target as HTMLElement;
            if (!dot.classList.contains('image-cards__carousel-dot')) return;
            const index = parseInt(dot.getAttribute('data-index') || '', 10);
            if (isNaN(index) || index < 0 || index >= cardElements.length) return;
            goToCard(index);
        });
        
        // Add event listeners for carousel navigation
        prevButton.addEventListener('click', goToPrevCard);
        nextButton.addEventListener('click', goToNextCard);
        
        // Add event listeners for dot navigation
        dotButtonsContainer.addEventListener('click', (event) => {
            const dot = event.target as HTMLElement;
            if (!dot.classList.contains('image-cards__carousel-dot')) return;
            const index = parseInt(dot.getAttribute('data-index') || '', 10);
            if (isNaN(index) || index < 0 || index >= cardElements.length) return;
            goToCard(index);

        });
        
        // Add resize listener to handle switching between desktop and mobile modes
        const handleResize = () => {         
            const newCarouselMode = this.checkCarouselMode();

            if (newCarouselMode !== this.isCarouselMode) {
                this.isCarouselMode = newCarouselMode;
                
                if (this.isCarouselMode) {
                    // Reset all styles and set positions for carousel mode
                    cardElements.forEach(card => {
                        card.style.transition = '';
                        card.style.opacity = '';
                        card.style.zIndex = '';
                    });
                    positionCards();
                } else {
                    // Reset all styles for desktop mode
                    cardElements.forEach(card => {
                        card.style.transition = '';
                        card.style.transform = '';
                        card.style.opacity = '';
                        card.style.zIndex = '';
                        card.classList.remove('active', 'prev', 'next');
                        setAriaHidden(card, null); // Remove aria-hidden attribute
                    });
                }
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        return {
            cleanup: () => {
                prevButton.removeEventListener('click', goToPrevCard);
                nextButton.removeEventListener('click', goToNextCard);
                window.removeEventListener('resize', handleResize);
            }
        };
    };
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.image-cards').forEach(element => {
        const cardElements = element.querySelectorAll('.image-card');
        const cards = Array.from(cardElements).map(cardElement => {
            // Create card objects using an object literal instead of constructor
            return {
                heading: cardElement.querySelector('.image-card__heading')?.textContent || '',
                imageUrl: cardElement.getAttribute('data-image-url') || '',
                title: cardElement.querySelector('.card-title')?.textContent || '',
                description: cardElement.querySelector('.image-card__text')?.textContent || ''
            } as IImageCard;
        });

        // Pass the options to the ImageCards constructor
        new ImageCards(element as HTMLElement, cards);
    });
});